import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { scopeOwnership } from "@/components/scope-picker/scope-sanity";
import type { ScopeRef } from "@/components/scope-picker/types";
import { useScopeTree } from "@/components/scope-picker/use-scope-tree";
import { AdminTable, type AdminTableColumn, type SortState } from "@/components/ui/admin-table";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/features/auth/use-auth";
import { BezStratyBadge } from "@/features/strateg-cenowy/components/BezStratyBadge";
import { ExportActions } from "@/features/strateg-cenowy/components/export-actions";
import { FilterBar, type FilterState } from "@/features/strateg-cenowy/components/filter-bar";
import { ImportDecylDialog } from "@/features/strateg-cenowy/components/import-decyl-dialog";
import { NetworkDecilesBadge } from "@/features/strateg-cenowy/components/NetworkDecilesBadge";
import { ResultsDetailDrawer } from "@/features/strateg-cenowy/components/results-detail-drawer";
import { StatisticsPanel } from "@/features/strateg-cenowy/components/statistics-panel";
import {
	StrategyButtons,
	type StrategyType,
} from "@/features/strateg-cenowy/components/strategy-buttons";
import {
	PriceUnitMismatchBadge,
	UnitCardBadge,
} from "@/features/strateg-cenowy/components/UnitCardBadge";
import { WynikiFinalPriceCell } from "@/features/strateg-cenowy/components/wyniki-final-price-cell";
import {
	rotacjaDniFromRow,
	StrategHeaderActions,
	WynikiMobileSortField,
	WynikiMobileToolbar,
	wynikiMobileRow,
} from "@/features/strateg-cenowy/components/wyniki-mobile";
import { useApplyStrategyMutation } from "@/features/strateg-cenowy/hooks/use-apply-strategy";
import { useScopePharmacyCount } from "@/features/strateg-cenowy/hooks/use-scope-pharmacy-count";
import { useStrategConfig } from "@/features/strateg-cenowy/hooks/use-strateg-config";
import {
	readPersistedFilters,
	useStrategFiltersPersistedState,
} from "@/features/strateg-cenowy/hooks/use-strateg-filters-persisted-state";
import {
	buildStrategResultsParams,
	strategResultsInfiniteOptions,
	useStrategResults,
} from "@/features/strateg-cenowy/hooks/use-strateg-results";
import { effectiveFinalPriceFromItem } from "@/features/strateg-cenowy/lib/effective-price";
import { canImportDecyl } from "@/features/strateg-cenowy/lib/import-decyl-access";
import { computeRowBadge, STRATEGY_BADGE_LABELS } from "@/features/strateg-cenowy/lib/row-badge";
import { strategCenowyApi } from "@/features/strateg-cenowy/strateg-api";
import type { ApplyStrategyRequest, StrategyResultItem } from "@/features/strateg-cenowy/types";
import { useStrategScope } from "@/features/strateg-cenowy/use-strateg-scope";
import { useVisibility } from "@/features/visibility/use-visibility";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { getCurrentUser } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";
import { decodeScope, encodeScope } from "@/lib/scope";
import { useDebounce } from "@/lib/use-debounce";
import { cn } from "@/lib/utils";

/** Human-readable label scope'a (do napisow w panelu / modalu bulk override). */
function scopeToLabel(s: ScopeRef): string {
	switch (s.type) {
		case "org":
			return "Cała organizacja";
		case "wlasne":
			return "Własne";
		case "franczyza":
			return "Franczyza";
		case "region":
			return s.id ? `Region ${s.id}` : "Region";
		case "spolka":
			return `Spółka ${s.id}`;
		case "apteka":
			return `Apteka #${s.id}`;
		case "orphan":
			return s.id ? `Grupa orphan ${s.id}` : "Grupa orphan";
		case "kr":
			return `KR: ${s.id}`;
	}
}

interface WynikiSearch {
	scope?: string;
	idfirm?: number;
	openImport?: boolean;
}

export const Route = createFileRoute("/_auth/strateg-cenowy/wyniki")({
	validateSearch: (search: Record<string, unknown>): WynikiSearch => {
		// Query strings są stringiem w URL — Number() coerce z guard.
		const idfirmRaw = search.idfirm;
		const idfirmNum =
			typeof idfirmRaw === "number"
				? idfirmRaw
				: typeof idfirmRaw === "string"
					? Number(idfirmRaw)
					: NaN;
		const openImportRaw = search.openImport;
		const openImport = openImportRaw === true || openImportRaw === "1" || openImportRaw === "true";
		return {
			scope: typeof search.scope === "string" ? search.scope : undefined,
			idfirm: Number.isFinite(idfirmNum) && idfirmNum > 0 ? idfirmNum : undefined,
			openImport: openImport || undefined,
		};
	},
	beforeLoad: async ({ context, location, search }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.strateg-cenowy.wyniki",
			location.pathname,
		);
		// Backward-compat shim: legacy URL z ?idfirm=NNN → przelicz na ?scope=apteka:NNN
		if (search.idfirm && !search.scope) {
			throw redirect({
				to: "/strateg-cenowy/wyniki",
				search: { scope: `apteka:${search.idfirm}` },
				replace: true,
			});
		}
	},
	loader: ({ context }) => {
		// Prefetch 1. strony wynikow dla persisted scope + filtrow. Z routerowym
		// `defaultPreload: "intent"` odpala sie juz na HOVER linku w sidebarze —
		// fetch leci rownolegle z dociaganiem chunka strony zamiast dopiero po
		// mount. Fire-and-forget (bez await): nawigacja nie czeka na dane,
		// mount przejmuje in-flight query przez identyczny queryKey
		// (buildStrategResultsParams = ta sama fabryka co w komponencie).
		const user = getCurrentUser();
		const scopeRaw =
			typeof window !== "undefined" ? window.localStorage.getItem("strateg-cenowy") : null;
		const scope = (scopeRaw ? decodeScope(scopeRaw) : null) ?? { type: "wlasne" as const };
		const persisted = readPersistedFilters(user?.id);
		void context.queryClient.prefetchInfiniteQuery(
			strategResultsInfiniteOptions(
				encodeScope(scope),
				buildStrategResultsParams({
					categories: persisted.categories,
					primaryCategoryCode: persisted.primaryCategoryCode,
					priceAction: persisted.priceAction,
					mechanism: persisted.mechanism,
				}),
			),
		);
	},
	component: StrategCenowyWynikiPage,
});

// ── Strategy mapping ────────────────────────────────────────────────────────

const STRATEGY_TO_API: Record<StrategyType, string> = {
	ekonomiczna: "avg-1-5",
	agresywna: "avg-1-2",
	podstawowa: "avg-3-5",
	manualna: "manual",
};

// ── Inline Decile Visualizer (10 bars + ghost bars + 3-period tooltip) ──────

const ANOMALY_THRESHOLD = 15; // % — próg wyświetlania ghost baru
const DECILE_KEYS = ["d1", "d2", "d3", "d4", "d5", "d6", "d7", "d8", "d9", "d10"];
const DECILE_BAR_W = 12; // w-3
const DECILE_GAP = 1; // gap-px

function contiguousRuns(indices: number[]): Array<[number, number]> {
	if (indices.length === 0) return [];
	const sorted = [...new Set(indices)].sort((a, b) => a - b);
	const runs: Array<[number, number]> = [];
	let start = sorted[0] as number;
	let end = start;
	for (let i = 1; i < sorted.length; i++) {
		const v = sorted[i] as number;
		if (v === end + 1) {
			end = v;
		} else {
			runs.push([start, end]);
			start = v;
			end = v;
		}
	}
	runs.push([start, end]);
	return runs;
}

function decileRangesForStrategy(
	strategy: string | undefined,
	manualDeciles: number[] | undefined,
): Array<[number, number]> {
	switch (strategy) {
		case "avg-1-2":
			return [[0, 1]];
		case "avg-3-5":
			return [[2, 4]];
		case "avg-1-5":
			return [[0, 4]];
		case "manual":
			return contiguousRuns(
				(manualDeciles ?? []).map((d) => d - 1).filter((d) => d >= 0 && d <= 9),
			);
		default:
			return [];
	}
}

function InlineDeciles({
	row,
	strategy,
	manualDeciles,
}: {
	row: StrategyResultItem;
	strategy?: string;
	manualDeciles?: number[];
}) {
	const history = row.history ?? [];
	const currEntry = history[0];
	const prevEntry = history[1];
	// Maly rynek lokalny (#475): slupki pokazuja decyle, z ktorych liczono cene.
	const network = row.networkDeciles;

	if (!currEntry && !network) {
		return <span className="text-muted-foreground text-xs">—</span>;
	}

	const localDeciles = currEntry?.values ?? [];
	const deciles: Array<number | null> = network ? network.deciles : localDeciles;
	const prevDeciles = network ? undefined : prevEntry?.values;
	const allCurr = deciles.filter((v): v is number => v != null && v > 0);
	const allPrev = prevDeciles?.filter((v): v is number => v != null && v > 0) ?? [];

	if (allCurr.length === 0) {
		return <span className="text-muted-foreground text-xs">—</span>;
	}

	const maxVal = Math.max(...allCurr, ...allPrev, 1);
	const BAR_H = 36;
	const ranges = decileRangesForStrategy(strategy, manualDeciles);

	return (
		<TooltipProvider delayDuration={0}>
			<div className="relative">
				{/* Ramka strategii: subtelne tlo + border za slupkami z wybranego zakresu decyli.
				    Dla strategii manualnej rendrujemy osobna ramke per ciagly segment z config.manualDeciles. */}
				{ranges.map(([from, to]) => {
					const left = from * (DECILE_BAR_W + DECILE_GAP) - 2;
					const width = (to - from + 1) * DECILE_BAR_W + (to - from) * DECILE_GAP + 4;
					return (
						<div
							key={`${from}-${to}`}
							aria-hidden="true"
							className="absolute pointer-events-none rounded transition-[left,width] duration-200 ease-out"
							style={{
								top: -3,
								bottom: -2,
								left: `${left}px`,
								width: `${width}px`,
								backgroundColor: "color-mix(in srgb, var(--primary) 7%, transparent)",
								border: "1px solid color-mix(in srgb, var(--primary) 22%, transparent)",
								zIndex: 0,
							}}
						/>
					);
				})}
				<div className="relative flex items-end gap-px" style={{ height: BAR_H }}>
					{deciles.map((value, i) => {
						const prevVal = prevDeciles?.[i] ?? null;
						const barH = value != null && value > 0 ? Math.max(3, (value / maxVal) * BAR_H) : 0;
						const ghostH =
							prevVal != null && prevVal > 0 ? Math.max(3, (prevVal / maxVal) * BAR_H) : 0;
						const isSelected = ranges.some(([a, b]) => i >= a && i <= b);

						const changePct =
							prevVal != null && prevVal > 0 && value != null
								? ((value - prevVal) / prevVal) * 100
								: undefined;
						const isDropAnomaly = changePct != null && changePct <= -ANOMALY_THRESHOLD;
						const isRiseAnomaly =
							changePct != null && !isDropAnomaly && changePct >= ANOMALY_THRESHOLD;

						// Ghost bars only for anomalies (|change| >= 15%)
						const showGhost = (isDropAnomaly || isRiseAnomaly) && ghostH > 0;

						// Tooltip: all periods for this decile index
						const tooltipHistory = history
							.map((h) => ({ period: h.period, value: h.values[i] }))
							.filter(
								(h): h is { period: string; value: number } => h.value != null && h.value > 0,
							);
						const localValue = localDeciles[i];
						const barRgb = network ? "8, 145, 178" : "22, 92, 58";

						return (
							<Tooltip key={DECILE_KEYS[i]}>
								<TooltipTrigger asChild>
									<div className="relative flex items-end cursor-help" style={{ height: BAR_H }}>
										{showGhost && isDropAnomaly && (
											<div
												className="absolute bottom-0 w-3 pointer-events-none"
												style={{
													height: ghostH,
													backgroundColor: "rgba(239, 68, 68, 0.25)",
													border: "1px dashed #ef4444",
													borderBottom: "none",
													borderRadius: "999px 999px 0 0",
													zIndex: 0,
												}}
											/>
										)}
										{showGhost && isRiseAnomaly && (
											<div
												className="absolute bottom-0 w-3 pointer-events-none"
												style={{
													height: ghostH,
													backgroundColor: "rgba(34, 197, 94, 0.3)",
													border: "1px dashed #22c55e",
													borderBottom: "none",
													borderRadius: "999px 999px 0 0",
													zIndex: 20,
												}}
											/>
										)}
										{barH > 0 && (
											<div
												className="relative w-3 z-10"
												style={{
													height: barH,
													borderRadius: "999px 999px 0 0",
													backgroundColor: isSelected
														? `rgba(${barRgb}, ${0.4 + (i / 9) * 0.6})`
														: `rgba(${barRgb}, ${0.2 + (i / 9) * 0.4})`,
												}}
											/>
										)}
									</div>
								</TooltipTrigger>
								{value != null && network && (
									<TooltipContent
										side="top"
										className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl"
									>
										<div className="font-semibold">
											Dec{i + 1} sieci: {value.toFixed(2)} zł
										</div>
										<div className="text-[10px] text-gray-300 mt-0.5">
											Mediana {network.pharmacyCount} aptek, raport {network.reportPeriod}
										</div>
										{localValue != null && localValue > 0 && (
											<div className="text-[10px] text-gray-400 mt-0.5">
												Rynek lokalny: {localValue.toFixed(2)} zł
											</div>
										)}
									</TooltipContent>
								)}
								{value != null && !network && currEntry && (
									<TooltipContent
										side="top"
										className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl"
									>
										<div className="font-semibold">
											Dec{i + 1}: {value.toFixed(2)} zl
										</div>
										{currEntry.period && (
											<div className="text-[10px] text-gray-300 mt-0.5">
												Raport: {currEntry.period}
											</div>
										)}
										{changePct != null && (
											<div
												className={cn(
													"font-medium mt-0.5",
													changePct < 0 ? "text-red-400" : "text-green-400",
												)}
											>
												Zmiana: {changePct > 0 ? "+" : ""}
												{changePct.toFixed(1)}%
											</div>
										)}
										{tooltipHistory.length > 1 && (
											<div className="mt-1.5 pt-1.5 border-t border-slate-700 text-[10px]">
												<div className="font-medium mb-0.5 text-gray-200">Historia:</div>
												{tooltipHistory.map((h, idx) => (
													<div
														key={h.period}
														className={idx === 0 ? "font-medium text-white" : "text-gray-400"}
													>
														{h.period}: {h.value.toFixed(2)} zl
													</div>
												))}
											</div>
										)}
									</TooltipContent>
								)}
							</Tooltip>
						);
					})}
				</div>
			</div>
		</TooltipProvider>
	);
}

// ── Market share mini bar ───────────────────────────────────────────────────

function MarketShareCell({ pct, sold, total }: { pct?: number; sold?: number; total?: number }) {
	if (pct == null) return <span className="text-muted-foreground text-xs">—</span>;

	const totalQty = (sold ?? 0) + (total ?? 0);

	return (
		<div className="space-y-1">
			<div className="text-[10px] text-muted-foreground tabular-nums">{pct.toFixed(1)}%</div>
			<div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
				<div
					className="h-full rounded-full bg-gray-900 transition-all"
					style={{ width: `${Math.min(100, pct)}%` }}
				/>
			</div>
			{sold != null && (
				<div className="text-[9px] text-muted-foreground/70 tabular-nums">
					{sold} / {totalQty}
				</div>
			)}
		</div>
	);
}

// ── Stock cell with rotation tooltip ────────────────────────────────────────

/**
 * Komorka kolumny "Stan" z tooltipem pokazujacym rotacje.
 * Rotacja = stan_apteki * 90 / sprzedaz_90d (z naszego MV product_sales_90d_by_pharmacy, sprz).
 */
function StockCell({ row }: { row: StrategyResultItem }) {
	const stock = row.pharmacyStock;
	if (stock == null) return <span className="text-muted-foreground text-xs">—</span>;

	const stockInt = Math.round(stock);
	const sales90d = row.farmasterSales90d ?? 0;
	const dailySales = sales90d / 90;
	const rotacjaDni = sales90d > 0 ? Math.round((stockInt * 90) / sales90d) : null;

	const isZero = stockInt === 0;
	const isLow = !isZero && stockInt < 5;

	const colorClass = isZero
		? "text-destructive"
		: isLow
			? "text-amber-600 dark:text-amber-500"
			: "text-foreground/80";

	const rotColor =
		rotacjaDni == null
			? "text-muted-foreground"
			: rotacjaDni <= 14
				? "text-destructive font-medium"
				: rotacjaDni <= 60
					? "text-amber-600 dark:text-amber-500"
					: "text-[var(--success)]";

	return (
		<TooltipProvider delayDuration={150}>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className={cn("text-xs tabular-nums font-medium cursor-help", colorClass)}>
						{stockInt} szt
					</span>
				</TooltipTrigger>
				<TooltipContent
					className="max-w-[280px] bg-popover/95 text-popover-foreground shadow-md border p-3"
					sideOffset={6}
				>
					<p className="text-sm font-semibold text-foreground">Rotacja zapasu</p>
					<div className="mt-1.5 space-y-0.5 text-xs text-muted-foreground tabular-nums">
						<div>
							Stan apteki: <span className="text-foreground">{stockInt} szt</span>
						</div>
						<div>
							Sprzedaz 90 dni (Farmaster):{" "}
							<span className="text-foreground">{sales90d.toFixed(0)} szt</span>
						</div>
						<div>
							Srednio dziennie:{" "}
							<span className="text-foreground">{dailySales.toFixed(2)} szt/dzien</span>
						</div>
					</div>
					{rotacjaDni != null ? (
						<p className={cn("mt-2 text-sm font-semibold tabular-nums", rotColor)}>
							{rotacjaDni} dni zapasu
						</p>
					) : (
						<p className="mt-2 text-xs text-muted-foreground">
							Brak sprzedazy w ostatnich 90 dniach — rotacja nieokreslona.
						</p>
					)}
					<p className="mt-2 text-[11px] text-muted-foreground/80 leading-snug">
						Wzor: stan / (zuzycie 60 dni / 60), zuzycie = sprzedaz w analogicznym 60-dniowym oknie
						rok temu (seasonal); fallback do rolling 90 dni gdy brak historii. Zielony = zapas {">"}{" "}
						60 dni, amber = 15-60, czerwony = ≤ 14. Spojne z panelem bocznym.
					</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

function RotationCell({ row }: { row: StrategyResultItem }) {
	const dni = rotacjaDniFromRow(row);
	if (dni == null) return <span className="text-muted-foreground text-xs">—</span>;

	const colorClass =
		dni <= 14
			? "text-destructive font-medium"
			: dni <= 60
				? "text-amber-600 dark:text-amber-500"
				: "text-[var(--success)]";

	return <span className={cn("text-xs tabular-nums font-medium", colorClass)}>{dni} dni</span>;
}

// ── Pharmacy selector ─────────────────────────────────────────────────────

// ── Columns ─────────────────────────────────────────────────────────────────

type Row = StrategyResultItem & { _index: number };

const columns: AdminTableColumn<Row>[] = [
	{
		key: "lp",
		header: "Lp",
		width: "60px",
		className: "text-center",
		sortable: true,
		hiddenOnMobile: true,
		render: (row) => <span className="font-mono text-xs text-muted-foreground">{row._index}</span>,
	},
	{
		key: "bloz07",
		header: "BLOZ",
		width: "80px",
		hiddenOnMobile: true,
		// Lista na telefonie: nazwa = tytul, BLOZ = podtytul (zamiast bloku udzialu).
		mobileRole: "subtitle",
		render: (row) => <span className="font-mono font-bold text-xs">{row.bloz07}</span>,
	},
	{
		key: "nazwa",
		header: "Nazwa Produktu",
		sortable: true,
		render: (row) => {
			const badge = computeRowBadge(row);

			const fullName = [row.nazwa, row.dawka, row.opakowanie].filter(Boolean).join(" ");
			return (
				<div className="min-w-0">
					<TooltipProvider delayDuration={200}>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="font-medium text-sm truncate cursor-default">
									{row.nazwa}
									{row.dawka && (
										<span className="ml-1 text-muted-foreground font-normal">{row.dawka}</span>
									)}
									{row.opakowanie && (
										<span className="ml-1 text-muted-foreground font-normal">{row.opakowanie}</span>
									)}
								</div>
							</TooltipTrigger>
							<TooltipContent side="top" className="max-w-md text-xs">
								{fullName}
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
					{(row.category || row.primaryCategoryLabel) && (
						<div className="flex items-center gap-1.5 mt-0.5 min-w-0">
							{row.category && (
								<span className="text-[10px] text-muted-foreground/80 shrink-0">
									{row.category}
								</span>
							)}
							{row.primaryCategoryLabel && (
								<span className="inline-flex items-center text-[10px] text-muted-foreground bg-muted/60 rounded px-1.5 py-0 truncate">
									{row.primaryCategoryCode} {row.primaryCategoryLabel}
								</span>
							)}
						</div>
					)}
					<div className="flex items-center gap-1 mt-0.5 flex-wrap">
						{row.ean && (
							<span className="text-[10px] text-muted-foreground tabular-nums">EAN: {row.ean}</span>
						)}
						{row.isRecepturaExcluded && (
							<span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0 rounded border bg-violet-50 text-violet-700 border-violet-300 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800/60">
								Receptura
							</span>
						)}
						{row.priceAction === "wypadajace" && row.history[0]?.period && (
							<span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0 rounded border bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800/60">
								Wypadl: {row.history[0].period}
							</span>
						)}
						{row.appliedMechanism === "bez_straty" && (
							<BezStratyBadge
								d1Base={row.bezStratyD1Base}
								finalPrice={row.finalPrice}
								previousPrice={row.pharmacyAvgPrice}
								reason={row.bezStratyReason}
								purchasePrice={row.purchasePriceMedian}
							/>
						)}
						{row.networkDeciles && <NetworkDecilesBadge usage={row.networkDeciles} />}
						{(row.unitCardPharmacies ?? 0) > 0 && (
							<UnitCardBadge
								multiplier={row.unitMultiplier}
								pharmacyCount={row.unitCardPharmacies ?? 0}
								finalPrice={row.finalPrice}
							/>
						)}
						{row.priceUnitMismatch && <PriceUnitMismatchBadge />}
						{badge && (row.appliedSourceCode || badge.secondary || badge.extras) ? (
							<>
								<TooltipProvider delayDuration={200}>
									<Tooltip>
										<TooltipTrigger asChild>
											<span
												className={cn(
													"inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0 rounded border",
													badge.color,
												)}
											>
												{badge.label}
											</span>
										</TooltipTrigger>
										<TooltipContent side="top" className="max-w-md text-xs">
											{badge.secondary
												? `Produkt w cenniku ${row.appliedSourceCennikName ?? row.appliedSourceLabel}. Cena z cennika: ${row.appliedSourcePrice?.toFixed(2)} zł. Aktualnie: ręczna edycja.`
												: `Cena z cennika ${row.appliedSourceCennikName ?? row.appliedSourceLabel} — strategia ${row.strategy ? STRATEGY_BADGE_LABELS[row.strategy] : "—"} pominieta.`}
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
								{badge.secondary && (
									<span
										className={cn(
											"inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0 rounded border",
											badge.secondary.color,
										)}
									>
										{badge.secondary.label}
									</span>
								)}
								{badge.extras?.map((extra) => (
									<span
										key={extra.label}
										className={cn(
											"inline-flex items-center text-[10px] font-semibold px-1.5 py-0 rounded border",
											extra.color,
										)}
									>
										{extra.label}
									</span>
								))}
							</>
						) : badge ? (
							<span
								className={cn(
									"inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0 rounded border",
									badge.color,
								)}
							>
								{badge.iconArrow === "down" && <ArrowDown className="size-2.5 text-destructive" />}
								{badge.iconArrow === "up" && <ArrowUp className="size-2.5 text-[var(--success)]" />}
								{badge.label}
							</span>
						) : null}
						<button
							type="button"
							className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:text-primary/80 cursor-pointer"
							onClick={(e) => e.stopPropagation()}
						>
							<Plus className="size-2.5" />
							Tag
						</button>
					</div>
					{row.tags.length > 0 && (
						<div className="flex gap-0.5 mt-0.5">
							{row.tags.slice(0, 3).map((t) => (
								<span
									key={t.id}
									className="text-[9px] text-muted-foreground bg-muted/60 rounded px-1"
								>
									{t.label}
								</span>
							))}
							{row.tags.length > 3 && (
								<span className="text-[9px] text-muted-foreground">+{row.tags.length - 3}</span>
							)}
						</div>
					)}
				</div>
			);
		},
	},
	{
		key: "market_share",
		header: "Udzial",
		width: "110px",
		sortable: true,
		render: (row) => (
			<MarketShareCell pct={row.marketSharePct} sold={row.soldCount} total={row.totalCount} />
		),
	},
	{
		key: "pharmacy_stock",
		header: "Stan",
		width: "80px",
		sortable: true,
		className: "text-right",
		hiddenOnMobile: true,
		render: (row) => <StockCell row={row} />,
	},
	{
		key: "rotacja_dni",
		header: "Rotacja",
		width: "75px",
		sortable: true,
		className: "text-right",
		hiddenOnMobile: true,
		render: (row) => <RotationCell row={row} />,
	},
	{
		key: "deciles",
		header: "Decyle (Rynek)",
		width: "200px",
		hiddenOnMobile: true,
		render: (row) => <InlineDeciles row={row} />,
	},
	{
		key: "gain_per_unit",
		header: "Zysk/szt",
		width: "90px",
		sortable: true,
		className: "text-right",
		hiddenOnMobile: true,
		render: (row) => {
			if (row.gainPerUnit == null) {
				return <span className="text-muted-foreground text-xs">—</span>;
			}
			const isNeg = row.gainPerUnit < 0;
			return (
				<div className="flex items-center justify-end gap-0.5">
					{isNeg ? (
						<ArrowDown className="size-3 text-destructive shrink-0" />
					) : row.gainPerUnit > 0 ? (
						<ArrowUp className="size-3 text-[var(--success)] shrink-0" />
					) : null}
					<span
						className={cn(
							"text-xs tabular-nums",
							isNeg ? "text-destructive" : "text-[var(--success)]",
						)}
					>
						{row.gainPerUnit.toFixed(2)} zl/szt
					</span>
				</div>
			);
		},
	},
	{
		key: "pharmacy_avg_price",
		header: "Sr. cena Apteki",
		width: "110px",
		className: "text-right",
		hiddenOnMobile: true,
		render: (row) => {
			const value =
				row.pharmacyAvgPrice != null
					? `${row.pharmacyAvgPrice.toFixed(2)} zl`
					: row.currentPrice != null
						? `${row.currentPrice.toFixed(2)} zl`
						: "—";
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="text-xs tabular-nums cursor-help">{value}</span>
					</TooltipTrigger>
					<TooltipContent side="left" className="max-w-xs text-xs">
						Średnia ważona ceny sprzedaży z ostatniego pełnego miesiąca (Farmaster, SUM(cena ×
						ilość)/SUM(ilość)). Fallback: mediana 90d, w ostateczności IQVIA.
					</TooltipContent>
				</Tooltip>
			);
		},
	},
	{
		key: "final_price",
		header: "Cena koncowa",
		width: "120px",
		sortable: true,
		className: "text-right bg-primary/5",
		render: (row) => {
			const final = effectiveFinalPriceFromItem(row);
			// Informacyjna "stara cena" z cennika wlasnego apteki (backend wypelnia
			// tylko gdy scope=apteka). Pokazujemy ja przekreslona NAD cena koncowa —
			// user widzi co ma juz ustawione w swoich cennikach. Tooltip: nazwa
			// cennika + data aktualizacji. Multi-pharmacy scope -> pole null, nic nie
			// pokazujemy. Gdy BLOZ wystepuje w kilku cennikach apteki backend bierze
			// najnowszy `dgakt`.
			const ownPrice =
				row.ownPriceListPrice != null ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<span className="block text-xs line-through text-muted-foreground tabular-nums cursor-help">
								{row.ownPriceListPrice.toFixed(2)} zl
							</span>
						</TooltipTrigger>
						<TooltipContent side="left" className="max-w-xs text-xs">
							Cena z cennika wlasnego apteki:{" "}
							<span className="font-medium">{row.ownPriceListName ?? "—"}</span>
							{row.ownPriceListDgakt ? (
								<span className="text-muted-foreground"> (akt. {row.ownPriceListDgakt})</span>
							) : null}
						</TooltipContent>
					</Tooltip>
				) : null;

			// Brak rekomendacji z silnika (np. produkt bez decyli IQVIA — typowo Rx).
			// Pokazujemy aktualna srednia apteczna jako "cena koncowa = utrzymaj",
			// stylem muted (nie bold primary) zeby user widzial, ze to NIE wynik
			// kalkulacji. Stats backendu pomijaja te produkty (skip w compute_inline_stats).
			if (final == null) {
				const fallback = row.pharmacyAvgPrice ?? row.currentPrice;
				if (fallback == null) {
					return (
						<div className="flex flex-col items-end gap-0.5">
							{ownPrice}
							<span className="text-muted-foreground text-xs">—</span>
						</div>
					);
				}
				return (
					<div className="flex flex-col items-end gap-0.5">
						{ownPrice}
						<span className="text-sm text-muted-foreground tabular-nums">
							{fallback.toFixed(2)} zl
						</span>
					</div>
				);
			}
			return (
				<div className="flex flex-col items-end gap-0.5">
					{ownPrice}
					<span className="text-base font-bold text-primary tabular-nums">
						{final.toFixed(2)} zl
					</span>
				</div>
			);
		},
	},
	{
		key: "purchase_price",
		header: "Cena zakupu",
		width: "95px",
		className: "text-right",
		hiddenOnMobile: true,
		render: (row) => (
			<span className="text-xs tabular-nums text-primary font-medium">
				{row.purchasePriceMedian != null ? `${row.purchasePriceMedian.toFixed(2)} zl` : "—"}
			</span>
		),
	},
	{
		key: "margin_pct",
		header: "Marza",
		width: "70px",
		sortable: true,
		className: "text-center",
		hiddenOnMobile: true,
		render: (row) => {
			if (row.marginPct == null) {
				return <span className="text-muted-foreground text-xs">—</span>;
			}
			const v = row.marginPct;
			const isNeg = v < 0;
			return (
				<span
					className={cn(
						"inline-flex items-center justify-center min-w-[42px] px-1.5 py-0.5 rounded-full text-[11px] font-semibold tabular-nums",
						isNeg
							? "bg-destructive/15 text-destructive"
							: "bg-[var(--status-success-bg)] text-[var(--success)]",
					)}
				>
					{v.toFixed(1)}%
				</span>
			);
		},
	},
];

// ── Page ────────────────────────────────────────────────────────────────────

function StrategCenowyWynikiPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { isVisible } = useVisibility();
	const canImport = canImportDecyl(user?.roles, isVisible);
	const search = Route.useSearch();
	// Trzymamy bloz07 w state, ale `selectedItem` MUSI byc wyprowadzony z items
	// (cache useStrategResults) — inaczej snapshot zostaje stale po mutacjach
	// (np. Cofnij). Panel czyta finalPrice z productMeta; gdy productMeta = useState
	// snapshot, optimistic setQueriesData nie dociera do panelu az do F5.
	const [selectedBloz07, setSelectedBloz07] = useState<string | null>(null);
	const [sort, setSort] = useState<SortState | null>(null);
	const [importOpen, setImportOpen] = useState(false);
	// Auto-open dialog gdy URL ma ?openImport=1 (deep-link z /konfiguracja/iqvia FileBrowser).
	// useEffect (NIE useState init) — auth ładuje sie async, na pierwszym render `user` może
	// jeszcze być null. Po otworzeniu czyścimy `openImport` z URL żeby F5 nie reotwierał i
	// shareowanie linku nie sprawiało chaosu (review-fix Codex med-1).
	useEffect(() => {
		if (canImport && search.openImport && !importOpen) {
			setImportOpen(true);
			navigate({
				to: "/strateg-cenowy/wyniki",
				search: (prev) => ({ ...prev, openImport: undefined }),
				replace: true,
			});
		}
	}, [canImport, search.openImport, importOpen, navigate]);
	// Scope picker w layoucie ustawia, tutaj tylko czytamy persisted state.
	const { scope } = useStrategScope();
	// Ownership scope'a — filtruje atrybuty zalegacza w pickerze kategorii
	// (3002 tylko franczyza, 3003 tylko wlasne). Scope mieszany → oba.
	const { data: scopeTree } = useScopeTree();
	const { hasOwn, hasFranchise } = useMemo(
		() => scopeOwnership(scope, scopeTree),
		[scope, scopeTree],
	);
	const isBelowMd = useIsBelowMd();
	const [statsCollapsed, setStatsCollapsed] = useState(() => isBelowMd);
	// Resync default na zmiane breakpointu (rotacja telefonu, resize okna).
	useEffect(() => {
		setStatsCollapsed(isBelowMd);
	}, [isBelowMd]);

	// Panel boczny v2 — scope-aware kontrakt (Phase 7.1).
	const encodedScope = encodeScope(scope);
	const scopeLabel = scopeToLabel(scope);
	const pharmacyCount = useScopePharmacyCount(scope);
	const { data: stConfig } = useStrategConfig(scope);
	const anomalyThresholdPct = stConfig?.anomalyThresholdFull ?? 15;
	const applyMutation = useApplyStrategyMutation(encodedScope);

	// Sticky filtry per-user w localStorage: categories (NRF/OTC/RX),
	// primaryCategoryCode, priceAction. search/tagId/showExcluded zostaja ad-hoc.
	// Key opiera sie o user.id, wiec dwoch uzytkownikow na tym samym kompie nie
	// dziedziczy swoich wyborow.
	//
	// `filters` jest DERIVED z persistedFilters (przez useMemo), NIE trzymamy go
	// w useState. Inaczej init strzela raz na pierwszym renderze gdy `user.id` jest
	// jeszcze undefined (key=anon, pusto), potem auth hydration zmienia key na
	// realny ale useState nie reaguje → filtry odswiezane localStorage'em znikaja.
	const [persistedFilters, setPersistedFilters] = useStrategFiltersPersistedState(user?.id);
	const [adHocFilters, setAdHocFilters] = useState<{
		search: string;
		tagId: number | null;
		showExcluded: boolean;
		centralSource: string | null;
		ownPriceList: boolean;
	}>({
		search: "",
		tagId: null,
		showExcluded: false,
		centralSource: null,
		ownPriceList: false,
	});
	const filters: FilterState = useMemo(
		() => ({
			search: adHocFilters.search,
			categories: persistedFilters.categories,
			primaryCategoryCode: persistedFilters.primaryCategoryCode,
			tagId: adHocFilters.tagId,
			showExcluded: adHocFilters.showExcluded,
			priceAction: persistedFilters.priceAction,
			mechanism: persistedFilters.mechanism,
			centralSource: adHocFilters.centralSource,
			ownPriceList: adHocFilters.ownPriceList,
		}),
		[adHocFilters, persistedFilters],
	);
	const setFilters = useCallback(
		(next: FilterState) => {
			setAdHocFilters({
				search: next.search,
				tagId: next.tagId,
				showExcluded: next.showExcluded,
				centralSource: next.centralSource,
				ownPriceList: next.ownPriceList,
			});
			setPersistedFilters({
				categories: next.categories,
				primaryCategoryCode: next.primaryCategoryCode,
				priceAction: next.priceAction,
				mechanism: next.mechanism,
			});
		},
		[setPersistedFilters],
	);

	const debouncedSearch = useDebounce(filters.search, 300);
	// Multi-select kategorii idzie do API jako pojedynczy string `NRF,OTC,RX`. Backend
	// rozdziela przecinkami i wydziela "RX" jako wirtualna kategoria (filter na items
	// bez decyli IQVIA). Pusta lista == brak filtra (undefined w queryparams), backend
	// zwraca wszystko (NRF + OTC + items bez decyli).
	const categoryParam = filters.categories.length > 0 ? filters.categories.join(",") : undefined;

	const handleStrategyChange = useCallback(
		(s: StrategyType) => {
			applyMutation.mutate({
				strategy: STRATEGY_TO_API[s] as ApplyStrategyRequest["strategy"],
				filters: {
					search: debouncedSearch || undefined,
					category: categoryParam,
					primaryCategoryCode: filters.primaryCategoryCode ?? undefined,
					priceAction: filters.priceAction ?? undefined,
					tagId: filters.tagId ?? undefined,
					showExcluded: filters.showExcluded || undefined,
					mechanism: filters.mechanism ?? undefined,
				},
			});
		},
		[
			applyMutation,
			debouncedSearch,
			categoryParam,
			filters.primaryCategoryCode,
			filters.priceAction,
			filters.tagId,
			filters.showExcluded,
			filters.mechanism,
		],
	);

	const handleReset = useCallback(() => {
		setFilters({
			search: "",
			categories: [],
			primaryCategoryCode: null,
			tagId: null,
			showExcluded: false,
			priceAction: null,
			mechanism: null,
			centralSource: null,
			ownPriceList: false,
		});
		setSort(null);
	}, [setFilters]);

	// buildStrategResultsParams — wspolna fabryka z route loaderem (prefetch);
	// rozjazd ksztaltu obiektu = inny hash queryKey = podwojny fetch na wejsciu.
	const queryParams = useMemo(
		() =>
			buildStrategResultsParams({
				search: debouncedSearch,
				categories: filters.categories,
				primaryCategoryCode: filters.primaryCategoryCode,
				tagId: filters.tagId,
				showExcluded: filters.showExcluded,
				priceAction: filters.priceAction,
				mechanism: filters.mechanism,
				centralSource: filters.centralSource,
				ownPriceList: filters.ownPriceList,
				sortBy: sort?.column,
				sortDir: sort?.dir,
			}),
		[debouncedSearch, filters, sort],
	);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isFetching,
		isPending,
		isPlaceholderData,
	} = useStrategResults(scope, queryParams);
	// Blur + overlay TYLKO gdy wyswietlane dane NIE odpowiadaja biezacym parametrom:
	// - `isPending`: pierwsze ladowanie, zero danych w cache;
	// - `isPlaceholderData`: `keepPreviousData` pokazuje snapshot poprzedniego
	//   scope'a/filtra do czasu przyjscia nowego — bez blura user widzialby
	//   "niewlasciwe" wiersze dla poprzedniej apteki przez 1-2s.
	// Background refetch TEGO SAMEGO klucza (reconciliation po edycji ceny /
	// bulk apply / SSE invalidation) NIE blurule — tabela ma juz optimistic
	// patch z mutacji i blur na 5s czynil edycje cen bezuzytecznie wolna.
	// Suppress takze gdy panel boczny otwarty — i tak nie widac tabeli pod drawer.
	const showLoadingOverlay = (isPending || (isFetching && isPlaceholderData)) && !selectedBloz07;

	const items = useMemo(
		() =>
			(data?.pages.flatMap((p) => p.items ?? []) ?? []).map((item, i) => ({
				...item,
				_index: i + 1,
			})),
		[data],
	);
	// selectedItem wyprowadzony z items — re-derives gdy drawer patchuje cache
	// (np. po Cofnij). useState snapshot pomijalby optimistic update az do F5.
	const selectedItem = useMemo(
		() => (selectedBloz07 ? (items.find((i) => i.bloz07 === selectedBloz07) ?? null) : null),
		[items, selectedBloz07],
	);
	const totalCount = data?.pages[0]?.total ?? 0;
	const inlineStats = data?.pages[0]?.stats;

	const handleRowHover = useCallback(
		(row: StrategyResultItem) => {
			queryClient.prefetchQuery({
				queryKey: ["strateg-cenowy", "detail", row.bloz07, encodedScope],
				queryFn: () => strategCenowyApi.getDetail(row.bloz07, encodedScope),
				staleTime: 60_000,
			});
		},
		[queryClient, encodedScope],
	);

	const rowClassName = useCallback(
		(row: Row) => (row.isAnomaly ? "border-l-2 border-l-amber-500 bg-amber-50/30" : undefined),
		[],
	);

	// Manual deciles z configu per scope (waterfall org/wlasne/apteka). Dla manualnej strategii
	// ramka rysuje sie wokol wybranych decyli — segmenty ciagle = osobne ramki.
	const { data: strategConfig } = useStrategConfig(scope);
	const manualDeciles = strategConfig?.manualDeciles;

	// Wstrzykuje strategie do kolumny "Decyle" — per-row z bazy z fallbackiem do config.defaultStrategy.
	const tableColumns = useMemo<AdminTableColumn<Row>[]>(
		() =>
			columns.map((col) => {
				if (col.key === "deciles") {
					return {
						...col,
						render: (row: Row) => (
							<InlineDeciles
								row={row}
								strategy={row.strategy ?? strategConfig?.defaultStrategy ?? "avg-1-5"}
								manualDeciles={manualDeciles}
							/>
						),
					};
				}
				if (col.key === "final_price") {
					return {
						...col,
						render: (row: Row) => (
							<WynikiFinalPriceCell
								row={row}
								encodedScope={encodedScope}
								canEdit={pharmacyCount === 1}
							/>
						),
					};
				}
				return col;
			}),
		[strategConfig?.defaultStrategy, manualDeciles, encodedScope, pharmacyCount],
	);

	const activeCategories =
		filters.categories.length > 0
			? (filters.categories as Array<"NRF" | "OTC" | "RX">)
			: (["NRF", "OTC", "RX"] as Array<"NRF" | "OTC" | "RX">);
	const renderMobileRow = useCallback(
		(row: Row) => wynikiMobileRow(row, sort?.column, rowClassName(row)),
		[sort?.column, rowClassName],
	);

	const importButton = canImport && (
		<Button
			variant="outline"
			size="sm"
			onClick={() => setImportOpen(true)}
			className="gap-1.5 max-md:relative max-md:h-9"
		>
			<Upload className="size-3.5" />
			{/* Telefon: sama ikona obok wyboru apteki (napis zostaje nazwa dostepna). */}
			<span className="max-md:sr-only">Wczytaj raport</span>
		</Button>
	);

	return (
		<div className="flex flex-col md:h-full md:overflow-hidden">
			{/* Telefon: tytul trafia do paska gornego, import i eksport do pasa
			    z wyborem apteki, statystyki i strategia do paska nad lista. */}
			{isBelowMd && <PageHeader title="Wyniki Kalkulacji" />}
			{isBelowMd ? (
				<StrategHeaderActions>
					{importButton}
					<ExportActions scope={encodedScope} params={queryParams} />
				</StrategHeaderActions>
			) : (
				/* Header — compact: title + stats toggle + export */
				<div className="px-3 md:px-6 pt-3 pb-1">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="flex flex-wrap items-baseline gap-3 min-w-0">
							<h1 className="text-lg font-semibold text-foreground shrink-0 max-md:hidden">
								Wyniki Kalkulacji
							</h1>
							<button
								type="button"
								onClick={() => setStatsCollapsed((v) => !v)}
								className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0 max-md:h-9"
							>
								<ChevronDown
									className={cn("size-3.5 transition-transform", statsCollapsed && "-rotate-90")}
								/>
								{statsCollapsed ? "Pokaż statystyki" : "Ukryj statystyki"}
							</button>
						</div>
						<div className="flex items-center gap-2 shrink-0">
							{importButton}
							<ExportActions scope={encodedScope} params={queryParams} />
						</div>
					</div>
					{!statsCollapsed && inlineStats && (
						<div
							className={cn(
								"mt-2",
								showLoadingOverlay && "pointer-events-none select-none blur-[2px]",
							)}
							aria-busy={showLoadingOverlay}
						>
							<StatisticsPanel
								stats={inlineStats}
								totalCount={totalCount}
								activeCategories={activeCategories}
							/>
						</div>
					)}
				</div>
			)}

			{/* Filter bar + strategy buttons; telefon: szukajka i chipy na calej
			    szerokosci, sortowanie i „Wyczyść” (dawne ↺) w arkuszu filtrow. */}
			{isBelowMd ? (
				<FilterBar
					filters={filters}
					onChange={setFilters}
					totalCount={totalCount}
					hasOwn={hasOwn}
					hasFranchise={hasFranchise}
					mobileSortField={<WynikiMobileSortField sort={sort} onSortChange={setSort} />}
					onReset={handleReset}
				/>
			) : (
				<div className="px-3 md:px-6 py-2 flex flex-col md:flex-row md:items-center gap-3">
					<div className="flex-1 min-w-0">
						<FilterBar
							filters={filters}
							onChange={setFilters}
							totalCount={totalCount}
							hasOwn={hasOwn}
							hasFranchise={hasFranchise}
						/>
					</div>
					<StrategyButtons
						onChange={handleStrategyChange}
						onReset={handleReset}
						scope={scope}
						isPending={applyMutation.isPending}
					/>
				</div>
			)}

			{/* Table */}
			<div
				className={
					isBelowMd ? "relative" : "relative flex-1 min-h-[60vh] md:min-h-0 px-3 md:px-6 pb-4 pt-1"
				}
			>
				<div
					className={cn(
						"h-full",
						showLoadingOverlay && "pointer-events-none select-none blur-[2px]",
					)}
					aria-busy={showLoadingOverlay}
				>
					<AdminTable
						tableId="strateg-cenowy-wyniki"
						columns={tableColumns}
						data={items}
						keyField="bloz07"
						emptyMessage="Brak wynikow dla wybranych filtrow."
						className={isBelowMd ? "rounded-none border-x-0" : "h-full"}
						rowClassName={rowClassName}
						onRowClick={(row) => setSelectedBloz07(row.bloz07)}
						onRowHover={handleRowHover}
						totalCount={totalCount}
						onFetchMore={fetchNextPage}
						hasNextPage={hasNextPage}
						isFetchingNextPage={isFetchingNextPage}
						sort={sort}
						onSortChange={setSort}
						// Telefon: lista z cena i udzialem, stukniecie otwiera panel produktu
						// (ten sam co na desktopie); tryb „Tabela” pod przelacznikiem.
						renderMobileRow={renderMobileRow}
						mobileOpenDirectly
						mobileToolbar={
							<WynikiMobileToolbar
								totalCount={totalCount}
								stats={inlineStats}
								activeCategories={activeCategories}
								scope={scope}
								onStrategyChange={handleStrategyChange}
								isStrategyPending={applyMutation.isPending}
							/>
						}
					/>
				</div>
				{showLoadingOverlay && (
					<div
						className="absolute inset-0 z-[60] flex items-center justify-center rounded-md bg-background/50 backdrop-blur-[1px]"
						data-testid="strateg-wyniki-loading-overlay"
					>
						<span className="animate-pulse text-xs font-bold uppercase tracking-widest text-muted-foreground">
							Pobieranie danych
						</span>
					</div>
				)}
			</div>

			<ResultsDetailDrawer
				bloz07={selectedBloz07}
				scope={encodedScope}
				scopeLabel={scopeLabel}
				pharmacyCount={pharmacyCount}
				anomalyThresholdPct={anomalyThresholdPct}
				productMeta={selectedItem ?? undefined}
				onClose={() => setSelectedBloz07(null)}
			/>

			{canImport && <ImportDecylDialog open={importOpen} onOpenChange={setImportOpen} />}
		</div>
	);
}
